/**
 * Support widget tickets (the real app proxies Jira Service Desk). Each
 * persona sees only the requests they raised, as the backend scopes them.
 */
import { iso, now } from '../../clock';
import { PERSONAS, Persona } from '../../personas';
import { getState } from '../../state';
import { daysAgoAt, friendly, jiraIso } from './time';

const SUPPORT_NAME = 'Dashboard Support';

type Category = 'NEW' | 'INDETERMINATE' | 'DONE';

const STATUS: Record<string, Category> = {
    'Waiting for support': 'NEW',
    'In Progress': 'INDETERMINATE',
    'Waiting for customer': 'INDETERMINATE',
    Resolved: 'DONE',
};

export interface TicketComment {
    id: string;
    body: string;
    authorName: string;
    created: Date;
}

export interface Ticket {
    issueId: string;
    issueKey: string;
    summary: string;
    description: string;
    pagePath: string;
    reporterEmail: string;
    reporterName: string;
    created: Date;
    updated: Date;
    status: string;
    unread: boolean;
    confirmed: boolean;
    comments: TicketComment[];
}

interface SeedComment {
    fromSupport: boolean;
    body: string;
    when: Date;
}

interface Seed {
    n: number;
    persona: string;
    summary: string;
    description: string;
    page: string;
    created: Date;
    status: string;
    unread: boolean;
    comments: SeedComment[];
}

function seeds(): Seed[] {
    return [
        {
            n: 116,
            persona: 'care',
            summary: 'Can incident categories match our new reporting form?',
            description: 'We changed the incident form in July. The categories on the Incidents page still use the old names, which confuses the team leaders when they compare the two.',
            page: '/clients/incidents',
            created: daysAgoAt(27, 11, 4),
            status: 'In Progress',
            unread: false,
            comments: [
                { fromSupport: true, body: 'Thanks Rebecca — could you send through the list of new category names?', when: daysAgoAt(26, 9, 40) },
                { fromSupport: false, body: 'Attached the new form. The main change is that "Cultural safety" is now its own category.', when: daysAgoAt(25, 14, 2) },
                { fromSupport: true, body: 'Got it. The category mapping is updated in testing; we will move it to production with the next release.', when: daysAgoAt(8, 10, 15) },
            ],
        },
        {
            n: 118,
            persona: 'ceo',
            summary: 'Board pack export cuts off the finance chart',
            description: 'When I print the board dashboard to PDF the finance chart is cut off on the right-hand side.',
            page: '/board',
            created: daysAgoAt(23, 10, 22),
            status: 'Resolved',
            unread: false,
            comments: [
                { fromSupport: true, body: 'Fixed in today\'s release — the chart now scales to the page width when printing. Could you confirm it prints correctly for you?', when: daysAgoAt(21, 15, 48) },
            ],
        },
        {
            n: 121,
            persona: 'coo',
            summary: 'Set up dashboard access for the new Warakurna team leader',
            description: 'Our new team leader at Warakurna started on Monday and needs Management access to the dashboard.',
            page: '/users',
            created: daysAgoAt(15, 8, 51),
            status: 'Resolved',
            unread: false,
            comments: [{ fromSupport: true, body: 'Account created with Management access and the sign-in details have been emailed to them.', when: daysAgoAt(15, 13, 30) }],
        },
        {
            n: 122,
            persona: 'board',
            summary: 'Dashboard will not load on my iPad',
            description: 'The board dashboard shows a blank white page on my iPad. It works on my laptop.',
            page: '/board',
            created: daysAgoAt(12, 19, 12),
            status: 'Resolved',
            unread: false,
            comments: [
                { fromSupport: true, body: 'Thanks Joan. The iPad was running an older version of Safari. After the iOS update the dashboard loads normally, so we have marked this resolved.', when: daysAgoAt(11, 9, 5) },
            ],
        },
        {
            n: 124,
            persona: 'ceo',
            summary: 'Add Tjanpi Desert Weavers sales to the finance overview',
            description: 'Could the finance page show Tjanpi sales income as its own line? The Board asks about it at every meeting.',
            page: '/finance',
            created: daysAgoAt(9, 14, 37),
            status: 'In Progress',
            unread: true,
            comments: [
                { fromSupport: true, body: 'We have scoped this with David. The Tjanpi income accounts are mapped and we are adding the sales line to the revenue breakdown now.', when: daysAgoAt(2, 11, 20) },
            ],
        },
        {
            n: 125,
            persona: 'hr',
            summary: 'Training page counts a retired course as overdue',
            description: 'Several staff show as overdue for the old manual handling course, which was replaced in August.',
            page: '/people/training',
            created: daysAgoAt(6, 9, 18),
            status: 'In Progress',
            unread: true,
            comments: [
                { fromSupport: true, body: 'Confirmed — the retired course is still being counted as mandatory. The fix is in testing and should be live this week.', when: daysAgoAt(1, 16, 5) },
            ],
        },
        {
            n: 127,
            persona: 'coo',
            summary: 'Incidents page lists Pukatja twice after the overnight sync',
            description: 'Since this morning Pukatja (Ernabella) appears twice in the incidents by community chart.',
            page: '/clients/incidents',
            created: daysAgoAt(4, 8, 2),
            status: 'Waiting for customer',
            unread: true,
            comments: [
                { fromSupport: true, body: 'We merged a duplicate site record that came through from the client system. Could you refresh the page and let us know if it still shows twice?', when: daysAgoAt(1, 10, 44) },
            ],
        },
        {
            n: 129,
            persona: 'care',
            summary: "Medication chart is missing Friday's entries for Amata",
            description: "The medications page doesn't show any administrations for Amata last Friday, but the team did record them.",
            page: '/clients/medications',
            created: daysAgoAt(2, 13, 26),
            status: 'Waiting for support',
            unread: false,
            comments: [],
        },
        {
            n: 130,
            persona: 'hr',
            summary: 'How do I export the compliance list to Excel?',
            description: 'I need the expiring checks list for the managers meeting. Is there a way to download it?',
            page: '/people/compliance',
            created: daysAgoAt(1, 9, 55),
            status: 'Waiting for customer',
            unread: true,
            comments: [
                {
                    fromSupport: true,
                    body: 'The CSV export on the Workplace Compliance page is currently only available to Admin users. We have asked Margaret whether HR should have it too — in the meantime she can download it for you.',
                    when: daysAgoAt(0, 8, 40),
                },
            ],
        },
    ];
}

function buildTickets(): Ticket[] {
    return seeds().map(s => {
        const p = PERSONAS.find(x => x.key === s.persona) || PERSONAS[0];
        const comments = s.comments.map((c, i) => ({
            id: String(20000 + s.n * 10 + i),
            body: c.fromSupport ? c.body : `*${p.name}* (${p.email}):\n\n${c.body}`,
            authorName: c.fromSupport ? SUPPORT_NAME : p.name,
            created: c.when,
        }));
        const updated = comments.length ? comments[comments.length - 1].created : s.created;
        return {
            issueId: String(10300 + s.n),
            issueKey: `SUP-${s.n}`,
            summary: s.summary,
            description: s.description,
            pagePath: s.page,
            reporterEmail: p.email,
            reporterName: p.name,
            created: s.created,
            updated,
            status: s.status,
            unread: s.unread,
            confirmed: false,
            comments,
        };
    });
}

/** Every ticket in this tab (all reporters). */
export function allTickets(): Ticket[] {
    return getState('admin:tickets', buildTickets);
}

export function ticketsFor(p: Persona): Ticket[] {
    return allTickets()
        .filter(t => t.reporterEmail === p.email && !t.confirmed)
        .sort((a, b) => b.updated.getTime() - a.updated.getTime());
}

export function findTicket(key: string): Ticket | undefined {
    return allTickets().find(t => t.issueKey === key);
}

const category = (t: Ticket): Category => STATUS[t.status] || 'INDETERMINATE';

function dateJson(d: Date): Record<string, unknown> {
    return { iso8601: jiraIso(d), jira: jiraIso(d), friendly: friendly(d), epochMillis: d.getTime() };
}

function fullDescription(t: Ticket): string {
    return `${t.description}\n\n----\nSubmitted via in-app widget\nReporter: ${t.reporterEmail}\nPage: ${t.pagePath}`;
}

function fieldValues(t: Ticket): Record<string, string>[] {
    return [
        { fieldId: 'summary', label: 'Summary', value: t.summary },
        { fieldId: 'description', label: 'Description', value: fullDescription(t) },
    ];
}

function commentJson(c: TicketComment): Record<string, unknown> {
    return { id: c.id, body: c.body, public: true, author: { displayName: c.authorName }, created: { friendly: friendly(c.created), iso8601: jiraIso(c.created) } };
}

export function ticketListRow(t: Ticket): Record<string, unknown> {
    return {
        issueId: t.issueId,
        issueKey: t.issueKey,
        summary: t.summary,
        createdDate: dateJson(t.created),
        updatedDate: dateJson(t.updated),
        currentStatus: { status: t.status, statusCategory: category(t).toLowerCase() },
        unread: t.unread,
    };
}

/** GET /api/support/tickets body for a persona. */
export function ticketList(p: Persona, limit = 20): Record<string, unknown> {
    const mine = ticketsFor(p);
    const values = mine.slice(0, Math.max(1, limit)).map(ticketListRow);
    return {
        values,
        size: values.length,
        limit,
        isLastPage: mine.length <= limit,
        nextPageToken: null,
        unreadCount: mine.filter(t => t.unread).length,
    };
}

/** GET /api/support/tickets/{key} body; opening a ticket marks it read. */
export function ticketDetail(t: Ticket): Record<string, unknown> {
    t.unread = false;
    const values = t.comments.map(commentJson);
    return {
        issueId: t.issueId,
        issueKey: t.issueKey,
        requestTypeId: '10',
        serviceDeskId: '1',
        createdDate: dateJson(t.created),
        reporter: { displayName: t.reporterName, emailAddress: t.reporterEmail },
        requestFieldValues: fieldValues(t),
        currentStatus: { status: t.status, statusCategory: category(t), statusDate: { friendly: friendly(t.updated), iso8601: jiraIso(t.updated) } },
        comments: { size: values.length, start: 0, limit: 50, isLastPage: true, values },
    };
}

export function createTicket(p: Persona, body: Record<string, any>): Record<string, unknown> {
    const all = allTickets();
    const n = Math.max(...all.map(t => Number(t.issueKey.replace('SUP-', '')))) + 1;
    const created = now();
    const t: Ticket = {
        issueId: String(10300 + n),
        issueKey: `SUP-${n}`,
        summary: String(body?.['summary'] || 'Support request').slice(0, 250),
        description: String(body?.['description'] || ''),
        pagePath: String(body?.['pagePath'] || '/'),
        reporterEmail: p.email,
        reporterName: p.name,
        created,
        updated: created,
        status: 'Waiting for support',
        unread: false,
        confirmed: false,
        comments: [],
    };
    all.push(t);
    return {
        issueId: t.issueId,
        issueKey: t.issueKey,
        requestTypeId: '10',
        serviceDeskId: '1',
        createdDate: { ...dateJson(created), friendly: 'Just now' },
        currentStatus: { status: t.status, statusCategory: 'NEW' },
        reporter: { displayName: p.name, emailAddress: p.email },
        requestFieldValues: fieldValues(t),
    };
}

/** Adds a comment as the persona; commenting on a resolved request re-opens it. */
export function addTicketComment(p: Persona, t: Ticket, text: string): Record<string, unknown> {
    const c: TicketComment = {
        id: String(20000 + Number(t.issueKey.replace('SUP-', '')) * 10 + t.comments.length + 50),
        body: `*${p.name}* (${p.email}):\n\n${text}`,
        authorName: p.name,
        created: now(),
    };
    t.comments.push(c);
    t.updated = c.created;
    if (category(t) === 'DONE') {
        t.status = 'In Progress';
    }
    return { ...commentJson(c), created: { friendly: 'Just now', iso8601: jiraIso(c.created) } };
}

export function isTicketDone(t: Ticket): boolean {
    return category(t) === 'DONE';
}

/** Org-wide support figures (for a CEO report or the assistant). */
export function supportTicketSummary(): { total: number; open: number; waitingOnSupport: number; waitingOnCustomer: number; inProgress: number; resolved: number; generatedAt: string } {
    const live = allTickets().filter(t => !t.confirmed);
    const count = (s: string) => live.filter(t => t.status === s).length;
    return {
        total: live.length,
        open: live.filter(t => category(t) !== 'DONE').length,
        waitingOnSupport: count('Waiting for support'),
        waitingOnCustomer: count('Waiting for customer'),
        inProgress: count('In Progress'),
        resolved: count('Resolved'),
        generatedAt: iso(now()),
    };
}
